// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Box, Button, Group, Image, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';
import watchpatImage from '../../assets/watchpat-device.png';
import { deriveSleepTestStatus, type SleepTestStatus } from '../../hooks/useSleepTestStatus';
import sectionClasses from './dashboard-section.module.css';
import classes from './SleepTestsSection.module.css';

interface SleepTestsSectionProps {
  hsatOrders: Parameters<typeof deriveSleepTestStatus>[0];
  hsatReports: Parameters<typeof deriveSleepTestStatus>[1];
  loading: boolean;
}

/**
 * Patient-side Sleep Tests panel. Shows a single info card describing the
 * active HSAT order's progression — Order Status and whether a result is
 * available — when one exists, or an empty-state message otherwise.
 */
export function SleepTestsSection({ hsatOrders, hsatReports, loading }: SleepTestsSectionProps): JSX.Element {
  const status = deriveSleepTestStatus(hsatOrders, hsatReports);
  let body: JSX.Element;
  if (loading) {
    body = (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  } else if (!status) {
    body = (
      <Text c="dimmed" py="md">
        You do not have any sleep tests in progress.
      </Text>
    );
  } else {
    body = <SleepTestCard status={status} />;
  }
  return (
    <Paper shadow="sm" radius="md" p="lg" className={sectionClasses.decorated}>
      <Title order={3} mb="md">
        Sleep Tests
      </Title>
      {body}
    </Paper>
  );
}

function SleepTestCard({ status }: { status: SleepTestStatus }): JSX.Element {
  const navigate = useNavigate();
  const needsApproval = status.orderStatus === 'patient-approval-pending';
  const serviceRequestId = status.serviceRequest.id;
  return (
    <Paper withBorder radius="md" p="md" bg="white">
      <Group align="center" justify="space-between" wrap="wrap" gap="lg">
        <Group align="center" gap="md" wrap="nowrap">
          <Image src={watchpatImage} alt="HSAT device" w={72} h={72} radius="md" fit="cover" />
          <Stack gap={2}>
            <Text fw={700}>{status.orderStatusLabel}</Text>
            <Text size="sm" c="dimmed">
              Order Number: {status.orderNumber}
            </Text>
            {needsApproval && serviceRequestId ? (
              <Button size="xs" mt={4} onClick={() => navigate(`/order-hsat/${serviceRequestId}`)}>
                Complete Your Order
              </Button>
            ) : (
              <Anchor size="sm">Report an Issue</Anchor>
            )}
          </Stack>
        </Group>
        <Group gap="sm" wrap="nowrap">
          <StatusCell label="Order Status" value={status.orderStatusLabel} />
          <StatusCell label="Result" value={status.resultAvailability} highlight />
        </Group>
      </Group>
    </Paper>
  );
}

interface StatusCellProps {
  label: string;
  value: string;
  highlight?: boolean;
}

function StatusCell({ label, value, highlight }: StatusCellProps): JSX.Element {
  return (
    <Box className={highlight ? classes.cellHighlight : classes.cell}>
      <Text size="xs" fw={600} className={classes.cellLabel}>
        {label}
      </Text>
      <Text fw={700} size="md" className={classes.cellValue}>
        {value}
      </Text>
    </Box>
  );
}
